import React, { useEffect, useState } from "react";
import { Modal, Table, Tag, Button, Tooltip, Empty } from "antd";
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { getSubscriptionPayments } from "../../services/subscription";
import { PAYMENT_STATUS, PAYMENT_STATUS_COLOR } from "../../constants/constants";
import { downloadFile } from "../../services/files";

interface PaymentRecord {
  id: number;
  amount: number;
  dueDate: string;
  paymentDate: string;
  status: string;
  receiptId: string;
  confirmed: boolean;
}

interface Props {
  visible: boolean;
  subscriptionId: number | null;
  subscriptionName: string;
  onCancel: () => void;
  onViewReceipt?: (receiptId: string) => void;
}

function SubscriptionPaymentsModal({
  visible,
  subscriptionId,
  subscriptionName,
  onCancel,
  onViewReceipt,
}: Props) {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [downloadingUrl, setDownloadingUrl] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (visible && subscriptionId) {
      fetchPayments();
    } else {
      setPayments([]);
      setDownloadingUrl({}); // Reset urls on close
    }
  }, [visible, subscriptionId]);

  const fetchPayments = async () => {
    if (!subscriptionId) return;
    try {
      setLoading(true);
      const result = await getSubscriptionPayments(subscriptionId);
      setPayments(result.data || []);
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFileUrl = async (path: string) => {
    if (downloadingUrl[path]) return; // already loaded
    try {
      const url = await downloadFile(path, path.endsWith('.pdf') ? "pdf" : "image");
      setDownloadingUrl(prev => ({ ...prev, [path]: url || "" }));
    } catch (err) {
      console.error("Error loading file url:", err);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: "Fecha de Emisión",
      dataIndex: "dueDate",
      key: "dueDate",
    },
    {
      title: "Fecha de Pago",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (date: string) => date || "—",
    },
    {
      title: "Monto",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => (
        <span style={{ fontWeight: "bold" }}>$ {amount?.toFixed(2)}</span>
      ),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={PAYMENT_STATUS_COLOR[status] || "default"}>
          {PAYMENT_STATUS[status] || status}
        </Tag>
      ),
    },
    {
      title: "Confirmado",
      dataIndex: "confirmed",
      key: "confirmed",
      align: "center" as const,
      render: (confirmed: boolean) =>
        confirmed ? (
          <Tooltip title="Pago confirmado">
            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 20 }} />
          </Tooltip>
        ) : (
          <Tooltip title="Pendiente de confirmación">
            <ClockCircleOutlined style={{ color: "#faad14", fontSize: 20 }} />
          </Tooltip>
        ),
    },
    {
      title: "Comprobante",
      dataIndex: "receiptId",
      key: "receiptId",
      render: (receiptId: string) => {
        if (!receiptId || receiptId.startsWith("Admin Assignment")) {
          return <span style={{ color: "#999" }}>—</span>;
        }
        
        const url = downloadingUrl[receiptId];
        
        return !url ? (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              loadFileUrl(receiptId);
              onViewReceipt?.(receiptId);
            }}
            size="small"
          >
            Ver
          </Button>
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
            <EyeOutlined /> Abrir Recibo
          </a>
        );
      },
    },
  ];

  return (
    <Modal
      title={`Pagos de Suscripción — ${subscriptionName}`}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="close" onClick={onCancel}>
          Cerrar
        </Button>,
      ]}
      destroyOnClose
    >
      <Table
        loading={loading}
        dataSource={payments}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: (
            <Empty
              description="No hay pagos registrados para esta suscripción"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        size="middle"
      />
    </Modal>
  );
}

export default SubscriptionPaymentsModal;
